/**
 * MonsterDataFormatters
 * 
 * A collection of utility functions for formatting monster data in consistent ways
 * across components. Handles special cases for complex data structures.
 */

/**
 * Convert Challenge Rating string to numeric value
 * Handles fractional CRs like "1/4" and "1/8"
 * @param {string|number} cr - Challenge rating as string or number
 * @returns {number} Numeric value of the challenge rating
 */
export const convertCRToNumber = (cr) => {
  if (typeof cr === 'number') return cr;
  
  // Handle fractional CRs
  if (cr && cr.includes && cr.includes('/')) {
    const [numerator, denominator] = cr.split('/');
    return parseInt(numerator) / parseInt(denominator);
  }
  
  return parseFloat(cr) || 0;
};

/**
 * Format Challenge Rating for display
 * @param {string|number} cr - Challenge rating
 * @returns {string} Formatted challenge rating string
 */
export const formatCR = (cr) => {
  if (cr === undefined || cr === null) return '0';
  return cr.toString();
};

/**
 * Calculate XP from Challenge Rating based on D&D 5e rules
 * @param {string|number} cr - Challenge rating
 * @returns {number} XP value for the given CR
 */
export const calculateXPFromCR = (cr) => {
  // Standard D&D 5e XP values by CR
  const xpByCR = {
    0: 0,
    '1/8': 25,
    '1/4': 50,
    '1/2': 100,
    1: 200,
    2: 450,
    3: 700,
    4: 1100,
    5: 1800,
    6: 2300,
    7: 2900,
    8: 3900,
    9: 5000,
    10: 5900,
    11: 7200,
    12: 8400,
    13: 10000,
    14: 11500,
    15: 13000,
    16: 15000,
    17: 18000,
    18: 20000,
    19: 22000,
    20: 25000,
    21: 33000,
    22: 41000,
    23: 50000,
    24: 62000,
    25: 75000,
    26: 90000,
    27: 105000,
    28: 120000,
    29: 135000,
    30: 155000
  };
  
  // Convert CR to string to handle both numeric and string keys
  const crKey = typeof cr === 'number' ? 
    (cr === 0.125 ? '1/8' : cr === 0.25 ? '1/4' : cr === 0.5 ? '1/2' : cr.toString()) : 
    cr;
  
  return xpByCR[crKey] || 0;
};

/**
 * Calculate ability score modifier
 * @param {number} score - Ability score value
 * @returns {number} Modifier value
 */
export const getAbilityModifier = (score) => {
  return Math.floor((score - 10) / 2);
};

/**
 * Format modifier with a + or - sign
 * @param {number} modifier - Modifier value
 * @returns {string} Formatted modifier string (e.g., "+2" or "-1")
 */
export const formatModifier = (modifier) => {
  return modifier >= 0 ? `+${modifier}` : `${modifier}`;
};

/**
 * Format attack type for display
 * @param {string} attackType - Raw attack type value
 * @returns {string} Human-readable attack type
 */
export const formatAttackType = (attackType) => {
  switch(attackType) {
    case 'melee': return 'Melee Weapon Attack';
    case 'meleeSpell': return 'Melee Spell Attack';
    case 'ranged': return 'Ranged Weapon Attack';
    case 'rangedSpell': return 'Ranged Spell Attack';
    case 'area': return 'Area Effect';
    case 'save': return 'Saving Throw';
    default: return attackType || '';
  }
};

/**
 * Format saving throw ability name
 * @param {string} saveType - Abbreviated or full ability name
 * @returns {string} Full ability name
 */
export const formatSaveType = (saveType) => {
  if (!saveType) return 'Ability';
  
  switch (saveType.toLowerCase()) {
    case 'str': case 'strength': return 'Strength';
    case 'dex': case 'dexterity': return 'Dexterity';
    case 'con': case 'constitution': return 'Constitution';
    case 'int': case 'intelligence': return 'Intelligence';
    case 'wis': case 'wisdom': return 'Wisdom';
    case 'cha': case 'charisma': return 'Charisma';
    default: return saveType;
  }
};

/**
 * Safely handle potentially nested object values
 * @param {any} value - Value to format safely
 * @returns {string} String representation of the value
 */
export const safeFormat = (value) => {
  if (value === undefined || value === null) return '';
  
  if (typeof value === 'object') {
    // If it has average/formula keys (like damage dice)
    if (value.average !== undefined || value.formula !== undefined) {
      return value.formula || value.average?.toString() || '0';
    }
    // If it's some other kind of object, convert to string safely
    return JSON.stringify(value);
  }
  
  return value.toString();
};

/**
 * Format a damage entry for display
 * @param {Object} damageEntry - Damage entry object
 * @returns {string} Formatted damage string
 */
export const formatDamage = (damageEntry) => {
  if (!damageEntry) return '';
  
  // Safely extract values
  const average = typeof damageEntry.average === 'object' 
    ? safeFormat(damageEntry.average) 
    : (damageEntry.average || 0);
  
  const formula = typeof damageEntry.formula === 'object'
    ? safeFormat(damageEntry.formula)
    : (damageEntry.formula || damageEntry.dice || '0');
  
  const type = typeof damageEntry.type === 'object'
    ? safeFormat(damageEntry.type)
    : (damageEntry.type || '');
    
  return `${Math.floor(average)} (${formula}) ${type} damage`;
};
